/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import {
  ISystemManagerFacade,
  SYSTEM_MANAGER_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/system-manager-facade-interface';
import { SystemManagerRowVm } from '../../models/system-manager-row-vm';
import { SystemManagerSystemsType } from '../../models/system-manager-systems';
import { ContainmentTypes } from '../../../../../../shared/ui/src/lib/enums/containment-items';
import { SystemManagerInfo } from '../../models/system-manager-model';
import { GlobalConstants, dialogMap } from '../../constants/global-constants';
import { SystemManagerExportReturnCodes } from '../../../../../../shared/ui/src/lib/enums/system-manager-export-return-codes';
import { ITextButton } from '../../../../../../shared/ui/src/lib/components/sysque-text-button/sysque-text-button-types';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { Subject, skip, take, takeUntil } from 'rxjs';
import { SystemManagerRowTemplateComponent } from './system-manager-row-template/system-manager-row-template.component';
import { DxoItemDraggingProperties } from './system-manager-types';
import { SystemInfo } from '../../../../../data-access/src/lib/domain/system-info';
import { SystemOptionsReturnCodes } from '../../sysque-system-category-options/sysque-system-category-options.component';
import { AlertStatus, IAlert } from "../../../../../../shared/ui/src/lib/components/alert/alert-types";

// This component contains the implementation of the System Manager dialog
@Component({
  selector: 'system-manager',
  templateUrl: './system-manager.component.html',
  styleUrls: ['./system-manager.component.scss'],
  standalone: false
})
export class SystemManagerComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  @ViewChildren(SystemManagerRowTemplateComponent) rowTemplates: QueryList<SystemManagerRowTemplateComponent>;

  //#region Public members
  public data: SystemManagerInfo = {
    importButton: '',
    columnsName: [],
    addButton: '',
    exportButton: '',
    cancelRequestButton: '',
  };
  public componentTitle = '';
  public systemsChecked: string[] = [];
  public systemsSwitched: { label: string; isSwitched: boolean }[] = [];
  public isChecked = false;
  public exportButtonClicked = false;
  public title = GlobalConstants.AllSystemKey;
  public specificItems = false;
  public specificMenuSystems: SystemManagerRowVm[] = [];
  public alertStatus: AlertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public showWarning = false;
  public visibleInvisibleSystems: SystemManagerRowVm[] = [];
  public menuItems: SystemManagerSystemsType[] = [];
  public alertHeight = 850;
  public nonAlertHeight = 780;
  public deletedSystemName = '';
  public duplicateSystemShape = '';
  public allSystemsSelected = true;
  public selectedItem = GlobalConstants.AllSystemKey;
  public isSearching = false;

  // The following variables are used for drag and drop functionality
  public draggedItemIndex: number | null = null;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  //#endregion

  //#region Private members

  private isShapeInfoNeeded = true;

  //Members used for the import button
  public filePath: string;
  private selectedFileName: string;
  private validFileName: string;
  private readonly closed$ = new Subject<void>();

  //#endregion

  constructor(
    @Inject(SYSTEM_MANAGER_FACADE_TOKEN) private systemManagerFacade: ISystemManagerFacade,
    private cdr: ChangeDetectorRef
  ) {}

  //#region Public methods

  public ngOnInit(): void {
    // Set dockable key to false
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.DockableKey, false);
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SystemManageKey, true);

    // Set system manager info
    this.data.addButton = GlobalConstants.AddSystemButton;
    this.data.importButton = GlobalConstants.ImportSystemsButton;
    this.data.columnsName = GlobalConstants.ColumnsName;
    this.data.exportButton = GlobalConstants.ExportButton;
    this.data.cancelRequestButton = GlobalConstants.CancelRequestButton;
    this.componentTitle = GlobalConstants.SYSTEM_MANAGER_DIALOG;

    window.addEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));

    // Get the visible and invisible systems
    this.getVisibleInvisibleSystems();
  }

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  //#region Buttons values

  /**
   * This method is used to get the values for the add button
   */
  public get addButtonValues(): ITextButton<void> {
    return {
      name: this.data.addButton,
      width: '100%',
      isBordered: true,
      imageSrc: 'assets/img/slide_add.png',
      click: () => {
        this.onAddSystemClicked();
      },
    };
  }

  /**
   * This method is used to get the values for the export button
   */
  public get exportSystemsButtonValues(): ITextButton<void> {
    return {
      name: this.data.exportButton,
      width: '100%',
      imageSrc: 'assets/img/export_system_icon.png',
      click: () => {
        this.onExportClick();
      },
    };
  }

  /**
   * This method is used to get the values for the cancel request button
   */
  public get cancelRequestButtonValues(): ITextButton<void> {
    return {
      name: this.data.cancelRequestButton,
      width: '100%',
      imageSrc: 'assets/img/export_system_icon.png',
      click: () => {
        this.onCancelRequestClick();
      },
    };
  }

  /**
   * This method is used to get the values for the import button
   */
  public get importSystemsButtonValues(): ITextButton<void> {
    return {
      name: this.data.importButton,
      width: '30%',
      imageSrc: 'assets/img/import_system_icon.png',
      click: () => {
        this.onImportClick();
      },
    };
  }

  //#endregion

  //#region System Manager Row Template methods

  /**
   * Called when the row checked value is changed
   * @param param0 - The emitted event
   */
  public onRowCheckedChange({ label, detail }): void {
    if (detail && !this.systemsChecked.includes(label)) {
      this.systemsChecked.push(label);
    }
    if (!detail && this.systemsChecked.includes(label)) {
      this.systemsChecked = this.systemsChecked.filter((system) => system !== label);
    }

    // For the system that was checked, the selected flag will be updated to true
    // System will be updated in the visibleInvisibleSystems array
    const system = this.visibleInvisibleSystems.find((system) => system.label === label);
    system.selected = this.systemsChecked.includes(label);

    // If the allSystemsSelected flag is false, the system will be updated in the specificMenuSystems array too
    if (!this.allSystemsSelected) {
      const system = this.specificMenuSystems.find((system) => system.label === label);
      system.selected = this.systemsChecked.includes(label);
    }
  }

  /**
   * Manages the switch change event
   * @param label - The label of the system
   */
  public onSwitchChange(label: string): void {
    // Update switch value for the system in the systemsSwitched array and the visibleInvisibleSystems/specificMenuSystems array
    // Update the selected flag for the system in the visibleInvisibleSystems array

    // Find the index of the system in the visibleInvisibleSystems array
    let index = this.visibleInvisibleSystems.findIndex((system) => system.label === label);

    // The switch value will be updated and the selected flag will be updated to flase in the visibleInvisibleSystems array
    // The selected flag will be updated to false because toggleSelected method will be called and will be set to true there
    this.visibleInvisibleSystems[index].selected = false;
    this.visibleInvisibleSystems[index].switchValue = !this.visibleInvisibleSystems[index].switchValue;

    // If the allSystemsSelected flag is false, the system will be updated in the specificMenuSystems array too
    if (!this.allSystemsSelected) {
      // Find the index of the system in the specificMenuSystems array
      index = this.specificMenuSystems.findIndex((system) => system.label === label);

      // The switch value will be updated and the selected flag will be updated to false in the specificMenuSystems array
    // The selected flag will be updated to false because toggleSelected method will be called and will be set to true there
      this.specificMenuSystems[index].selected = false;
      this.specificMenuSystems[index].switchValue = !this.specificMenuSystems[index].switchValue;
    }

    // The switch value will be updated in the systemsSwitched array
    this.changeSwitchValue(label);
  }

  /**
   * Handles the context menu visibility
   * @param label The label of the system
   */
  public handleContextMenuVisibility(label: string): void {
    // When a context menu is opened, all the other context menus will be closed
    this.rowTemplates.forEach((rowTemplate) => {
      if (rowTemplate.rowVm.label !== label) {
        rowTemplate.contextMenu.hideContextMenu();
      }
    });

    // Get the index of the system for which the context menu is opened
    const index = this.allSystemsSelected
      ? this.visibleInvisibleSystems.find((system) => system.label === label).index
      : this.specificMenuSystems.find((system) => system.label === label).index;

    // The system for which the context menu is opened will be set as selected
    this.toggleSelected(index);

    // Even if the context menu is closed, the system will remain selected
    const systems = this.allSystemsSelected ? this.visibleInvisibleSystems : this.specificMenuSystems;
    const systemToSelect = systems.find((system) => system.label === label);

    if (systemToSelect) {
      systemToSelect.selected = true;
    }
  }

  /**
   * Deletes a system
   * @param item - The system to be deleted
   */
  public deleteSystemLogic(item: string): void {
    // Get the state, update selected system and update the local storage
    const state = this.systemManagerFacade.getState();
    state.selectedSystem = item;
    this.deletedSystemName = item;
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SCOPE, GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    let sysquePopupConfig = new SysquePopupConfig();

    // Create a new popup dialog configuration
    createDialog(sysquePopupConfig, GlobalConstants.DELETE_SYSTEM_NAME_DIALOG);
    this.systemManagerFacade.onSystemManagerItemClick(
      this.getDialogRoute(GlobalConstants.DELETE_SYSTEM_NAME_DIALOG),
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
  }

  /**
   * Renames a system
   * @param system - The system to be renamed
   */
  public renameSystem(system: string): void {
    // Get the state, update selected system and update the local storage
    const state = this.systemManagerFacade.getState();
    const systemRow = this.visibleInvisibleSystems.find((sys) => sys.label === system);
    state.selectedSystem = system;
    state.selectedCategory = systemRow?.shape;
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SCOPE, GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    let sysquePopupConfig = new SysquePopupConfig();

    // Create a new popup dialog configuration
    createDialog(sysquePopupConfig, GlobalConstants.RENAME_SYSTEM_NAME_DIALOG);
    this.systemManagerFacade.onSystemManagerItemClick(
      this.getDialogRoute(GlobalConstants.RENAME_SYSTEM_NAME_DIALOG),
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
  }

  /**
   * Duplicates a system
   * @param system - The system to be duplicated
   */
  public duplicateSystem(system: string): void {
    // Get the state, update selected system and update the local storage
    const state = this.systemManagerFacade.getState();
    const systemRow = this.visibleInvisibleSystems.find((sys) => sys.label === system);
    state.selectedSystem = system;
    state.selectedCategory = this.duplicateSystemShape = systemRow?.shape;
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    let sysquePopupConfig = new SysquePopupConfig();

    // Create a new popup dialog configuration
    createDialog(sysquePopupConfig, GlobalConstants.DUPLICATE_SYSTEM_NAME_DIALOG);
    this.systemManagerFacade.onSystemManagerItemClick(
      this.getDialogRoute(GlobalConstants.DUPLICATE_SYSTEM_NAME_DIALOG),
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
  }

  /**
   * Opens the dialog for setting the component priority
   * @param system - The system for which the component priority will be set
   */
  public setComponentPriority(system: string): void {
    // Get the state, update selected system and update the local storage
    const state = this.systemManagerFacade.getState();
    const systemRow = this.visibleInvisibleSystems.find((sys) => sys.label === system);
    state.selectedSystem = system;
    state.selectedCategory = systemRow?.shape;
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.systemManagerFacade.updateLocalStorage(GlobalConstants.SCOPE, GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    let sysquePopupConfig = new SysquePopupConfig();

    // Create a new popup dialog configuration
    createDialog(sysquePopupConfig, GlobalConstants.CHANGE_PRIORITY_DIALOG);
    this.systemManagerFacade.onSystemManagerItemClick(
      this.getDialogRoute(GlobalConstants.CHANGE_PRIORITY_DIALOG),
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
  }

  //#endregion

  /**
   * Handles the click event of the item from the left panel
   * @param event - The event emitted by the item click with the name of the item clicked
   */
  public handleItemClick(event: { itemData: { name: string } }): void {
    // The name of the item clicked will be set as the selected item
    this.selectedItem = event.itemData.name;

    // This will handle the event click of an item from the left panel
    let temporaryMenuSystems: SystemManagerRowVm[] = [];

    // If the item clicked is 'All systems' the title of the right panel container will be changed and the specificItems flag will be set to false
    if (event.itemData.name === GlobalConstants.AllSystemKey) {
      this.title = GlobalConstants.AllSystemKey;
      this.specificItems = false;

      // All the systems will be unselected
      this.visibleInvisibleSystems = this.visibleInvisibleSystems.map((system) => ({ ...system, selected: false }));

      // If the item clicked is 'All systems' the allSystemsSelected flag will be set to true
      this.allSystemsSelected = true;

      // The switch value of the system will be updated based on the systemsSwitched array
      this.setSwitchValue();

      return;
    }

    // If the item clicked is not 'All systems' the allSystemsSelected flag will be set to false
    this.allSystemsSelected = false;

    // For the rest of the items a temporary object is created
    // it will contain all of the systems that have the system shape equal to item name clicked
    this.title = event.itemData.name;

    // Based on this flag, in the html, the dataSource will be set to 'visibleInvisibleSystems' or 'specificMenuSystems'
    this.specificItems = true;
    this.visibleInvisibleSystems.forEach((system) => {
      // If the item clicked name is 'Containment' it will check if the system shape is in ContainmentTypes enum
      if (
        event.itemData.name === system.shape ||
        (event.itemData.name === GlobalConstants.ContainmentKey &&
          Object.values(ContainmentTypes).includes(system.shape as ContainmentTypes))
      ) {
        this.pushToTemporaryMenuSystems(temporaryMenuSystems, { ...system });
      }
    });

    this.specificMenuSystems = temporaryMenuSystems;

    // All the systems will be unselected
    this.specificMenuSystems = this.specificMenuSystems.map((system) => ({ ...system, selected: false }));

    // The switch value of the system will be updated based on the systemsSwitched array
    this.setSwitchValue();
  }

  /**
   * Handles the click event of the sub item from the left panel
   * @param event - The event emitted by the sub item click
   * @param item - The sub item clicked
   */
  public handleSubItemClick(event: Event, item: string): void {
    // The name of the sub item clicked will be set as the selected item
    this.selectedItem = item;

    // This will handle the event click of a sub item of an item from the left panel
    let temporaryMenuSystems: SystemManagerRowVm[] = [];

    // This was added to prevent the propagation to handleItemClick method too, when a subItem is clicked
    event.stopPropagation();

    this.allSystemsSelected = false;
    this.title = item;

    // Based on this flag, in the html, the dataSource will be set to 'visibleInvisibleSystems' or 'specificMenuSystems'
    this.specificItems = true;
    this.visibleInvisibleSystems.forEach((system) => {
      if (item === system.shape) {
        this.pushToTemporaryMenuSystems(temporaryMenuSystems, { ...system });
      }
    });

    this.specificMenuSystems = temporaryMenuSystems;

    // All the systems will be unselected
    this.specificMenuSystems = this.specificMenuSystems.map((system) => ({ ...system, selected: false }));

    // The switch value of the system will be updated based on the systemsSwitched array
    this.setSwitchValue();
  }

  /**
   * Reorder the systems after drag and drop
   * @param e - The event emitted by the sortable component
   */
  public reorderLogic: DxoItemDraggingProperties['onReorder'] = (e) => {
    // If the item is dropped in the same place, do nothing
    if (e.toIndex === this.draggedItemIndex) {
      this.draggedItemIndex = null;
      return;
    }

    // Get the start and end index of the dragged item
    const startIndex = e.fromIndex;
    const endIndex = e.toIndex;

    if (startIndex < endIndex) {
      // Update list of visible and invisible systems after drag and drop from top to bottom
      this.dragAndDropFromTopToBottom(startIndex, endIndex);
    } else {
      // Update list of visible and invisible systems after drag and drop from bottom to top
      this.dragAndDropFromBottomToTop(startIndex, endIndex);
    }

    // Reset the dragged item index
    this.draggedItemIndex = null;
  };

  /**
   * When the user starts dragging an item, the index of the dragged item will be stored
   * @param e - The event emitted by the sortable component
   */
  public onDragStart: DxoItemDraggingProperties['onDragStart'] = (e) => {
    // When the user starts dragging an item, the context menu will be closed
    this.hideContextMenu();
    this.cdr.detectChanges();
    
    // Prevent dragging when searching
    if (this.isSearching) {
      e.cancel = true; 
      return;
    }

    // The index of the dragged item will be stored
    this.draggedItemIndex = e.fromIndex;
  };

  /**
   * Handles the search event and updates the isSearching property.
   * @param event - The event object containing details of the search event.
   */
  public handleSearch(event: any): void {
    if (event.fullName === 'searchValue') {
      this.isSearching = event.value && event.value.length > 0;
      this.cdr.detectChanges();
    }
  }

  /**
   * Open the dialog for adding a new system
   */
  public onAddSystemClicked(): void {
    let sysquePopupConfig = new SysquePopupConfig();

    // Create a new popup dialog configuration
    createDialog(sysquePopupConfig, GlobalConstants.CREATE_SYSTEM_NAME_DIALOG);
    this.systemManagerFacade.onSystemManagerItemClick(
      this.getDialogRoute(GlobalConstants.CREATE_SYSTEM_NAME_DIALOG),
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
  }

  /**
   * This method is used to handle the click event of the import button
   * Get the file path and check if is valid
   * If the file is valid, update the local storage with the file path and open the import systems dialog
   */
  public onImportClick(): void {
    // Browse the file and get the file path
    this.systemManagerFacade
      .browseFile()
      .pipe(skip(1))
      .subscribe((response) => {
        // Check if the file path is invalid
        if (response === SystemManagerExportReturnCodes[SystemManagerExportReturnCodes.InvalidFileSelection]) {
          return;
        }

        this.filePath = response;
        this.selectedFileName = response.split('\\').pop();
        const fileExtension = this.selectedFileName.split('.').pop();
        const allowedExtension = '.SYSELECTRICAL';

        // Check if the selected file is a SYSELECTRICAL file
        if (allowedExtension !== `.${fileExtension}`) {
          // Clear the selected file name
          this.validFileName = '';

          // Show a toast notification with an error message
          this.showAlert('Please select a valid SYSELECTRICAL file', AlertStatus.Warning);
        } else {
          this.validFileName = this.selectedFileName;
        }
        if (this.validFileName) {
          // Update the local storage with the file path
          this.systemManagerFacade.updateLocalStorage(GlobalConstants.IMPORT_SYSTEMS_FILE, this.filePath);

          // Create a new popup dialog configuration
          let sysquePopupConfig = new SysquePopupConfig();
          createDialog(sysquePopupConfig, GlobalConstants.IMPORT_SYSTEMS_DIALOG);
          this.systemManagerFacade.onSystemManagerItemClick(
            this.getDialogRoute(GlobalConstants.IMPORT_SYSTEMS_DIALOG),
            sysquePopupConfig.width,
            sysquePopupConfig.height
          );
        }
      });
  }

  /**
   * This method is used to handle the click event of the export button
   */
  public onExportClick(): void {
    // Check if the export button is clicked for the first time
    if (!this.exportButtonClicked) {
      this.exportButtonClicked = true;

      // All the systems will be unselected
      this.resetSelectedSystems();

      return;
    }

    // If the export button was not clicked for the first time it will call the facade to apply the logic for export button clicked
    this.systemManagerFacade
      .onExportClick(this.systemsChecked)
      .pipe(takeUntil(this.closed$))
      .subscribe((response) => {
        if (response === SystemManagerExportReturnCodes.Success) {
          this.showAlert(GlobalConstants.SystemExportSuccess, AlertStatus.Success);

          // All the systems will be unselected and unchecked
          this.resetSelectedSystems();
          this.systemsChecked = [];
        } else if (response === SystemManagerExportReturnCodes.NotSysQueSystem) {
          this.showAlert(GlobalConstants.NoSysQueSystem, AlertStatus.Warning);
        }
      });
  }

  /**
   * This method is used to handle the click event of the cancel request button
   */
  public onCancelRequestClick(): void {
    this.exportButtonClicked = false;

    // All the systems will be unselected
    this.resetSelectedSystems();
  }

  /**
   * Select or unselect all systems
   * @param event - The event emitted by the checkbox column change
   */
  public onCheckboxChange(event: Event): void {
    this.isChecked = (event.target as HTMLInputElement).checked;

    const systems = this.allSystemsSelected ? this.visibleInvisibleSystems : this.specificMenuSystems;

    const updatedSystems = systems.map((system) => ({
      ...system,
      selected: this.isChecked,
    }));

    // All systems will be selected or unselected
    if (this.allSystemsSelected) {
      this.visibleInvisibleSystems = updatedSystems;
    } else {
      this.specificMenuSystems = updatedSystems;
    }
  }

  /**
   * This method is used to handle the click event of the close button
   */
  public closeWindow(): void {
    // Method used to emit the close event
    this.close.emit();
  }

  /**
   * This method is used to get the shapes for the left panel
   */
  public getShapes(): void {
    // This method is used to get the shapes for the left panel
    const itemMap = new Map<string, SystemManagerSystemsType>();

    // The option 'All system' is added as it's not dependent of the system shape
    this.menuItems.push(new SystemManagerSystemsType(GlobalConstants.AllSystemKey));
    itemMap.set(GlobalConstants.AllSystemKey, this.menuItems[0]);

    // Take each system from the 'visibleInvisibleSystems' object to check the system shape and push it to menuItems
    this.visibleInvisibleSystems.forEach((system) => {
      // First we check that the system shape is not already in the menuItems
      if (!itemMap.has(system.shape)) {
        // If the system shape is not in the menuItems, we add it
        this.addSystemShape(itemMap, system);
      }
    });

    // Move the ContainmentKey item to the end of the menuItems list
    this.moveContainmentKeyToEnd();
  }

  /**
   * This method is used to handle the click event of the dismiss button from the alert message
   */
  public onDismissClick(): void {
    this.systemManagerFacade.onWindowResize(this.nonAlertHeight);
    this.warningMessage = '';
    this.showWarning = false;
  }

  /**
   * Show an alert message
   * @param message - The message to be shown
   * @param alertStatus - The type of the alert
   */
  public showAlert(message: string, alertStatus: AlertStatus): void {
    this.systemManagerFacade.onWindowResize(this.alertHeight);
    this.warningMessage = message;
    this.showWarning = true;
    this.alertStatus = alertStatus;

    setTimeout(() => {
      this.onDismissClick();
    }, 5000);
  }

  /**
   * This method is used to handle the click event of the discard changes button
   */
  public onDiscardChangesClick(): void {
    // If the discard changes button is clicked,
    // The visible and invisible systems will be updated to the original state
    this.isShapeInfoNeeded = false;
    this.getVisibleInvisibleSystems();
    this.resetSelectedSystems();
    this.setSwitchValue();
    this.updateSpecificMenuSystems();
  }

  /**
   * This method is used to handle the click event of the save changes button
   */
  public onSaveChangesClick(): void {
    // If the save changes button is clicked,
    // The visible and invisible systems will be updated to the new state
    const systems: SystemInfo[] = this.getListOfSystemInfo();

    // Call the facade to save the reordered systems and systems visibility
    this.systemManagerFacade
      .saveSystemsChanges(systems)
      .pipe(take(1))
      .subscribe((result) => {
        if (result === SystemOptionsReturnCodes.Success) {
          this.showAlert(GlobalConstants.ChangesSaveSuccessfully, AlertStatus.Success);
          // If the systems operations was successful, the local storage will be updated
          localStorage.removeItem(GlobalConstants.SYSTEM_MANAGER_OPERATIONS_SUCCESS);
          localStorage.setItem(GlobalConstants.SYSTEM_MANAGER_OPERATIONS_SUCCESS, 'true');
          // All the systems will be unselected
          this.resetSelectedSystems();
        } else {
          this.showAlert(GlobalConstants.ReorderingFailed, AlertStatus.Warning);
        }
      });
  }

  /**
   * Used to handle the storage event listener
   * @param event the storage event
   */
  public storageEventListener(event: StorageEvent): void {
    if (!event) return;
    this.isShapeInfoNeeded = false;

    switch (event.key) {
      case GlobalConstants.IMPORT_SYSTEMS_SUCCESS:
      case GlobalConstants.SYSTEM_CREATION_SUCCESS:
      case GlobalConstants.SYSTEM_DUPLICATED_SUCCESS:
      case GlobalConstants.SYSTEM_RENAMED_SUCCESS:
        // If the create/import was successful, the visible and invisible systems and specific systems will be updated
        this.getVisibleInvisibleSystems();
        this.updateSpecificMenuSystems();
        break;
      case GlobalConstants.SYSTEM_DELETED_SUCCESS:
        // If the system was deleted, the all systems list and the specific menu systems will be updated
        this.visibleInvisibleSystems = this.visibleInvisibleSystems.filter(
          (system) => system.label !== this.deletedSystemName
        );
        this.specificMenuSystems = this.specificMenuSystems.filter((system) => system.label !== this.deletedSystemName);
        break;
    }
    this.cdr.detectChanges();
  }

  /**
   * Check if the system is one of the last three elements in the list
   * @param order - The order (id) of the system
   * @returns - True if the system is one of the last three elements in the list, false otherwise
   */
  public needContextMenuUpward(order: string): boolean {
    // Check the last elements from the visibleInvisibleSystems or specificMenuSystems
    // If the allSystemsSelected flag is true, the last element will be check from the visibleInvisibleSystems
    // If the allSystemsSelected flag is false, the last element will be check from the specificMenuSystems
    // If the length of the list is 5, the last element will be checked
    // If the length of the list is 6, the last two elements will be checked
    // If the length of the list is 7 or more, the last three elements will be checked
    const systems = this.allSystemsSelected ? this.visibleInvisibleSystems : this.specificMenuSystems;
    const length = systems.length;

    if (length === 5) {
      return systems[length - 1].order === order;
    } else if (length === 6) {
      return systems.slice(-2).some((system) => system.order === order);
    } else if (length >= 7) {
      return systems.slice(-3).some((system) => system.order === order);
    }

    return false;
  }

  /**
   * Set clicked system as selected and unselect the rest of the systems
   * @param index - The index of the system
   */
  public toggleSelected(index: string): void {
    // If the allSystemsSelected flag is true, the selected system will be checked from the visibleInvisibleSystems
    // If the allSystemsSelected flag is false, the selected system will be checked from the specificMenuSystems
    if (this.allSystemsSelected && !this.exportButtonClicked) {
      this.visibleInvisibleSystems.forEach((system) => {
        if (system.index !== index) {
          // If the system is not the selected one, it will be unselected
          system.selected = false;
        } else {
          // If the system is selected, it will be unselected and otherwise
          system.selected = !system.selected;
        }
      });
    } else if (!this.allSystemsSelected && !this.exportButtonClicked) {
      this.specificMenuSystems.forEach((system) => {
        if (system.index !== index) {
          // If the system is not the selected one, it will be unselected
          system.selected = false;
        } else {
          // If the system is selected, it will be unselected and otherwise
          system.selected = !system.selected;
        }
      });
    }
  }

  /**
   * Get the styling class for the row
   * @param rowVm - The row for which the class is needed
   * @returns - The class for the row
   */
  public getRowClass(rowVm: { order: string; selected: boolean }): string {
    const position = this.getPosition(rowVm.order);
    if (position % 2 !== 0 && rowVm.selected) {
      return GlobalConstants.UNBANDED_BACKGROUND_SELECTED;
    } else if (position % 2 !== 0 && !rowVm.selected) {
      return GlobalConstants.UNBANDED_BACKGROUND_UNSELECTED;
    } else if (position % 2 === 0 && rowVm.selected) {
      return GlobalConstants.BANDED_BACKGROUND_SELECTED;
    } else {
      return GlobalConstants.BANDED_BACKGROUND_UNSELECTED;
    }
  }

  //#endregion

  //#region Private methods

  private pushToTemporaryMenuSystems(temporaryMenuSystems: SystemManagerRowVm[], system: SystemManagerRowVm): void {
    // Method used to push a system in the given object
    temporaryMenuSystems.push({
      index: system.index,
      label: system.label,
      order: system.order,
      switchValue: false,
      shape: system.shape,
    });
  }

  /**
   * Get the visible and invisible systems from the backend
   */
  private getVisibleInvisibleSystems(): void {
    // Reset the visibleInvisibleSystems array and the systemsSwitched array
    this.systemsSwitched = [];
    this.visibleInvisibleSystems = [];

    // Call the facade to make a request to backend to take the information needed for visible&invisible systems from XML file
    this.systemManagerFacade
      .getVisibleInvisibleSystems()
      .pipe(takeUntil(this.closed$))
      .subscribe((systems) => {
        let dataSource: SystemManagerRowVm[] = [];
        let countRow = 1;
        systems.forEach((system) => {
          dataSource.push({
            index: system.id,
            label: system.name,
            order: countRow.toString(),
            switchValue: system.inVisibility === 'True',
            shape: system.systemShape,
          });

          // Set the switch value for the systems in the systemsSwitched array
          this.systemsSwitched.push({ label: system.name, isSwitched: system.inVisibility === 'True' });
          countRow += 1;
        });
        this.visibleInvisibleSystems = dataSource;
        if (this.visibleInvisibleSystems.length !== 0 && this.isShapeInfoNeeded) {
          this.getShapes();
        }
      });
  }

  /**
   * Get the route for the dialog
   * @param route - The route to be checked
   * @returns - The route for the dialog
   */
  private getDialogRoute(route: string): string {
    const values = Array.from(dialogMap.values());
    const keys = Array.from(dialogMap.keys());
    if (values.filter((v) => v === route).length > 0) {
      const routingKey = keys.find((key) => dialogMap.get(key) === route);
      return `dialog/${routingKey}`;
    }
  }

  /**
   * Converts the list of visible and invisible systems to a list of system info
   * @returns - The list of system info
   */
  private getListOfSystemInfo(): SystemInfo[] {
    let systems: SystemInfo[] = [];
    this.visibleInvisibleSystems.forEach((system) => {
      systems.push({
        name: system.label,
        id: system.order,
        unitType: '',
        inVisibility: system.switchValue ? 'true' : 'false',
        systemShape: system.shape,
        isHangerEnabled: false,
        isClearance: false,
      });
    });

    return systems;
  }

  /**
   * Update the visibleInvisibleSystems array after drag and drop from top to bottom
   * @param startIndex start index of the dragged item
   * @param endIndex end index of the dragged item
   */
  private dragAndDropFromTopToBottom(startIndex: number, endIndex: number): void {
    let selectedItems: SystemManagerRowVm[] = [];
    let allSystems = this.visibleInvisibleSystems;

    // Get the selected items
    selectedItems = allSystems.slice(startIndex, endIndex + 1);

    // Check if there are no selected items
    if (selectedItems.length === 0) {
      return;
    }

    // Get the first item
    const firstItem = selectedItems.shift();
    const firstOrder = firstItem.order;

    // Update first item with index and order from last item
    firstItem.order = selectedItems[selectedItems.length - 1].order;

    // Update the rest of the items
    const selectedItemsCopy = JSON.parse(JSON.stringify(selectedItems));
    for (let index = 1; index < selectedItems.length; index++) {
      selectedItems[index].order = selectedItemsCopy[index - 1].order;
    }

    // Update second item with index and order from first item
    selectedItems[0].order = firstOrder;

    // Add the updated first item to the end of the array
    selectedItems.push(firstItem);

    // Update the visibleInvisibleSystems array
    this.visibleInvisibleSystems.splice(startIndex, endIndex - startIndex + 1, ...selectedItems);

    // Update the position of the systems based on the order
    this.changePositionOfSystems();
  }

  /**
   * Update the visibleInvisibleSystems array after drag and drop from bottom to top
   * @param startIndex start index of the dragged item
   * @param endIndex end index of the dragged item
   */
  private dragAndDropFromBottomToTop(startIndex: number, endIndex: number): void {
    let selectedItems: SystemManagerRowVm[] = [];
    let allSystems = this.visibleInvisibleSystems;
    // Get the selected items
    selectedItems = allSystems.slice(endIndex, startIndex + 1);

    // Check if there are no selected items
    if (selectedItems.length === 0) {
      return;
    }

    // Get the last item
    const lastItem = selectedItems.pop();
    const lastOrder = lastItem.order;

    // Update last item with index and order from first item
    lastItem.order = selectedItems[0].order;

    // Update the rest of the items
    const selectedItemsCopy = JSON.parse(JSON.stringify(selectedItems));
    for (let index = 0; index < selectedItems.length - 1; index++) {
      selectedItems[index].order = selectedItemsCopy[index + 1].order;
    }

    // Update last but one item with index and order from last item
    selectedItems[selectedItems.length - 1].order = lastOrder;

    // Add the updated first item to the end of the array
    selectedItems.unshift(lastItem);

    // Update the visibleInvisibleSystems array
    this.visibleInvisibleSystems.splice(endIndex, startIndex - endIndex + 1, ...selectedItems);

    // Update the position of the systems based on the order
    this.changePositionOfSystems();
  }

  /**
   * Reset the selected systems to false
   */
  private resetSelectedSystems(): void {
    const updatedSystems = this.allSystemsSelected
      ? this.visibleInvisibleSystems.map((system) => ({ ...system, selected: false }))
      : this.specificMenuSystems.map((system) => ({ ...system, selected: false }));

    // All the systems will be unselected
    if (this.allSystemsSelected) {
      this.visibleInvisibleSystems = updatedSystems;
    } else {
      this.specificMenuSystems = updatedSystems;
    }
  }

  /**
   * Set the switch value of the system based on the systemsSwitched array
   * @param label - The label of the system
   */
  private setSwitchValue(): void {
    // Set the switch value of the system based on the systemsSwitched array
    // If the system is in the systemsSwitched array, the switch value will be true
    // If the system is not in the systemsSwitched array, the switch value will be false
    if (this.allSystemsSelected) {
      this.visibleInvisibleSystems = this.visibleInvisibleSystems.map((system) => {
        return {
          ...system,
          switchValue: this.systemsSwitched[this.isLabelInSystemsSwitched(system.label)].isSwitched,
        };
      });
    } else {
      this.specificMenuSystems = this.specificMenuSystems.map((system) => {
        return {
          ...system,
          switchValue: this.systemsSwitched[this.isLabelInSystemsSwitched(system.label)].isSwitched,
        };
      });
    }
  }

  /**
   * Get the position of the system in the list
   * @param order - The index of the system
   * @returns - The position of the system in the list
   */
  private getPosition(order: string): number {
    // If the allSystemsSelected flag is true, the position will be taken from the visibleInvisibleSystems
    // If the allSystemsSelected flag is false, the position will be taken from the specificMenuSystems
    return this.allSystemsSelected
      ? this.visibleInvisibleSystems.findIndex((system) => system.order === order)
      : this.specificMenuSystems.findIndex((system) => system.order === order);
  }

  /**
   * Check if the label is in the systemsSwitched array
   * @param label - The label of the system
   * @returns - The index of the system in the systemsSwitched array
   */
  private isLabelInSystemsSwitched(label: string): number {
    return this.systemsSwitched?.findIndex((system) => system.label === label);
  }

  /**
   * Change the switch value of the system
   * @param label - The label of the system whose switch value will be changed
   */
  private changeSwitchValue(label: string): void {
    const index = this.isLabelInSystemsSwitched(label);
    const isSwitched = this.systemsSwitched[index].isSwitched;
    this.systemsSwitched[index].isSwitched = !isSwitched;
  }

  /**
   * Change the position of the systems based on the order
   */
  private changePositionOfSystems(): void {
    this.visibleInvisibleSystems.forEach((fitting) => {
      fitting.index = fitting.order;
    });
  }

  /**
   * Hide the context menu for all systems
   */
  private hideContextMenu() {
    // For each row template, the context menu will be closed
    this.rowTemplates.forEach((rowTemplate) => {
      rowTemplate.contextMenu.hideContextMenu();
    });
  }

  /**
   * Update the specific menu systems
   */
  private updateSpecificMenuSystems(): void {
    if(this.allSystemsSelected){
      return;
    }
    const shape = this.specificMenuSystems[0]?.shape;

    this.specificMenuSystems = [];
    
    this.visibleInvisibleSystems.forEach((system) => {
      if(system.shape === shape){
        this.specificMenuSystems.push({ ...system });
      }
    });
  }

  /**
   * Add the system shape to the menu items
   * @param itemMap - The map with the menu items
   * @param system - The system for which the shape will be added as a sub item
   */
  private addSystemShape(itemMap: Map<string, SystemManagerSystemsType>, system: SystemManagerRowVm){
    // If one of the Containment types is found, then the shape is added as a sub item in the Containment menu item
    if (Object.values(ContainmentTypes).includes(system.shape as ContainmentTypes)) {
      this.addContainmentType(itemMap, system);
    } else {
      // Else the system shape will be added as an item in the menu item
      const newItem = new SystemManagerSystemsType(system.shape);
      this.menuItems.push(newItem);
      itemMap.set(system.shape, newItem);
    }
  }

  /**
   * Add the system shape as a sub item in the Containment menu item
   * @param itemMap - The map with the menu items
   * @param system - The system for which the shape will be added as a sub item
   */
  private addContainmentType(itemMap: Map<string, SystemManagerSystemsType>, system: SystemManagerRowVm): void {
    // If the ContainmentKey item is not in the menuItems, it will be added
    // If the ContainmentKey item is in the menuItems, the system shape will be added in the subItems array if it's not already there
    if (!itemMap.has(GlobalConstants.ContainmentKey)) {
      const newItem = new SystemManagerSystemsType(GlobalConstants.ContainmentKey, system.shape, true);
      this.menuItems.push(newItem);
      itemMap.set(GlobalConstants.ContainmentKey, newItem);
    } else {
      const item = itemMap.get(GlobalConstants.ContainmentKey);
      if (item && !item.subItems.includes(system.shape)) {
        item.subItems.push(system.shape);
      }
    }
  }

  /**
   * Move the ContainmentKey item to the end of the menuItems 
   */
  private moveContainmentKeyToEnd(): void {
    // Find the index of the ContainmentKey item
    const containmentIndex = this.menuItems.findIndex(item => item.name === GlobalConstants.ContainmentKey);

    // If the ContainmentKey item is found, remove it from the menuItems array and add it at the end
    if (containmentIndex !== -1) {
      const [containmentItem] = this.menuItems.splice(containmentIndex, 1);
      this.menuItems.push(containmentItem);
    }
  }

  //#endregion
}